import { createElement } from 'react';
import type { AlertColor } from '@mui/material/Alert';
import { alpha, createTheme, Theme } from '@mui/material/styles';

import { PiWarningDiamond } from 'react-icons/pi';
import { MdInfoOutline } from "react-icons/md";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { MdOutlineDangerous } from "react-icons/md";

const ALERT_VARIANTS: AlertColor[] = ['success', 'info', 'warning', 'error'];

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const alertIconMapping: Partial<Record<AlertColor, React.ReactNode>> = {
  info: createElement(MdInfoOutline),
  success: createElement(IoCheckmarkCircleOutline),
  warning: createElement(PiWarningDiamond),
  error: createElement(MdOutlineDangerous),
};

const customizedAlertClassExclusions = ALERT_VARIANTS.flatMap((variant) => {
  const capitalized = capitalize(variant);
  return [
    `.MuiAlert-standard${capitalized}`,
    `.MuiAlert-filled${capitalized}`,
    `.MuiAlert-outlined${capitalized}`,
  ];
})
  .map((className) => `:not(${className})`)
  .join('');

const variantLinearProgressStyles = (theme: Theme, variant: AlertColor) => ({
  backgroundColor: theme.palette[`alert_${variant}`].light,
  '@keyframes linearProgressVariantBar1': {
    '0%': { left: '-35%', right: '100%' },
    '60%': { left: '100%', right: '-90%' },
    '100%': { left: '100%', right: '-90%' },
  },
  '& .MuiLinearProgress-bar1': {
    backgroundColor: theme.palette[`alert_${variant}`].main,
    width: 'auto',
    animation: 'linearProgressVariantBar1 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite',
  },
  '& .MuiLinearProgress-bar2': {
    display: 'none',
  },
});

const alertStyles = (theme: Theme, props_variant: AlertColor) => ({
  backgroundColor: theme.palette[`alert_${props_variant}`].light,
  boxShadow: "0px 1px 2px -1px #0000001A, 0px 1px 3px 0px #0000001A",
  color: theme.palette.text.primary,
  maxWidth: '520px',
  borderRadius: '10px',
  alignItems: 'flex-start',
  padding: '16px 16px 18px 16px',
  '& .MuiAlert-icon': {
    color: theme.palette[`alert_${props_variant}`].main,
    paddingTop: '0px',
    paddingBottom: "0px",
    opacity: 1,
    '& .MuiSvgIcon-root': {
      color: theme.palette[`alert_${props_variant}`].main,
    },
  },
  '& .MuiAlert-message': {
    color: theme.palette[`alert_${props_variant}`].dark,
    fontSize: 'var(--font-size-md)',
    fontWeight: 400,
    lineHeight: 1.5,
    padding: 0,
  },
  '& .MuiAlertTitle-root': {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: 'var(--font-size-md)',
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.5),
  },
  '& .MuiAlert-action': {
    alignItems: 'flex-start',
    paddingTop: theme.spacing(0.25),
    marginRight: 0,
    '& .MuiIconButton-root': {
      color: theme.palette.text.secondary,
      padding: theme.spacing(0.5),
    },
    '& .MuiButton-root': {
      backgroundColor: theme.palette[`alert_${props_variant}`].light,
      color: '#FFFFFF',
      borderRadius: '8px',
      fontSize: 'var(--font-size-md)',
      fontWeight: 500,
      textTransform: 'none',
      padding: theme.spacing(0.75, 2),
      minWidth: 'unset',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#2D333C',
        boxShadow: 'none',
      },
    },
  },
});

/**
 * RESPONSIVE TYPOGRAPHY SYSTEM
 * Uses the same CSS custom properties as Tailwind for consistency.
 * Font sizes automatically scale based on screen size.
 * 
 * Breakpoints:
 * - xs (0px): Mobile - smallest sizes
 * - sm (640px): Tablet - slightly larger
 * - md (768px): Small laptop - medium sizes
 * - lg (1024px): Desktop - full sizes
 */

// Base theme without responsive adjustments
const baseTheme = createTheme({
  // Define breakpoints to match Tailwind
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#343d50', // chat-text-primary
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#EEF1F3', // secondary
      contrastText: '#2D2D2D',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2D2D2D',
      secondary: '#626b7f', // chat-text
    },
    divider: '#D1D7E0', // chat-border
    alert_info: {
      main: '#2C99FF',
      light: '#FFFFFF',
      dark: '#3A424D',
      contrastText: '#2D2D2D',
    },
    alert_success: {
      main: '#166534',
      light: '#DCFCE7',
      dark: '#3A424D',
      contrastText: '#2D2D2D',
    },
    alert_warning: {
      main: '#C75B20',
      light: '#FFF0D5',
      dark: '#3A424D',
      contrastText: '#2D2D2D',
    },
    alert_error: {
      main: '#981919',
      light: '#FEE2E2',
      dark: '#3A424D',
      contrastText: '#2D2D2D',
    },
    custom: {
      cardDark: '#3A424D', // dark card background
      cardBorder: '#E7ECF2', // light card border
      iconMuted: '#ACB0BF', // muted icon color
      tabBackground: '#E0E3E9', // tab container background
      safeGreen: '#5AB34B',
      safeLightGreen: '#F7FDF4',
      brandTeal: '#00A89D', // brand accent / category icon color
      violetMain: '#7C3AED', // Ask Reve brand accent
      violetLight: '#F5F3FF', // Ask Reve brand accent, hover/light background
      violetGradientStart: '#272366', // Ask Reve send button gradient start
      violetGradientMid: '#9158CE', // Ask Reve send button gradient end
      violetGradientHover: '#8B5CF6', // Ask Reve send button hover gradient
      pinkMain: '#DB2777', // ticket message accent (non-agent avatar)
      pinkLight: '#FCE7F3', // ticket message accent, avatar background
      statusOrangeMain: '#9A3412', // status chip text, e.g. "awaiting reply"
      statusOrangeLight: '#FFEDD5', // status chip background, e.g. "awaiting reply"
      statusBlueMain: '#1E40AF', // status chip text, e.g. "in progress"
      statusBlueLight: '#DBEAFE', // status chip background, e.g. "in progress"
      statusGray: '#6B7280', // status chip text, e.g. "closed"
      ticketAgentCardBackground: '#F8FAFC', // ticket message card background for agent messages
      fileWordBlue: '#2B579A', // Word document file-type icon
      fileExcelGreen: '#217346', // Excel spreadsheet file-type icon
      selectedRowBackground: '#f0f0f0', // selected list/table row background
    },
  },
  typography: {
    fontFamily: [
      'Manrope',
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    // Use CSS custom properties for responsive font sizes (shared with Tailwind)
    h1: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-4xl)',
      fontWeight: 800,
      lineHeight: 1.1,
    },
    h2: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-3xl)',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h3: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-xl)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-lg)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-base)',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-base)',
      lineHeight: 1.5,
    },
    body2: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-sm)',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-lg)',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-md)',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    caption: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-xs)',
      lineHeight: 1.4,
    },
    overline: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-2xs)',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      lineHeight: 1.4,
    },
    button: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: 'var(--font-size-base)',
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRight: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: 'Manrope, sans-serif',
          fontSize: 'var(--font-size-base)',
          borderRadius: '8px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'Manrope, sans-serif',
          fontSize: 'var(--font-size-base)',
        },
        label: {
          fontSize: 'var(--font-size-base)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          fontFamily: 'Manrope, sans-serif',
          borderRadius: '8px',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          variants: [
            {
              props: { variant: 'segmented' },
              style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                '& .MuiToggleButtonGroup-grouped': {
                  margin: 0,
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                },
                '& .MuiToggleButton-root': {
                  padding: theme.spacing(0.5),
                  minWidth: 0,
                  textTransform: 'none',
                  fontWeight: 400,
                  '&.Mui-selected': {
                    fontWeight: 700,
                    borderColor: theme.palette.custom.brandTeal,
                    backgroundColor: alpha(theme.palette.custom.brandTeal, 0.08),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.custom.brandTeal, 0.12),
                    },
                  },
                },
              },
            },
          ],
        }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
        })
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: 'Manrope, sans-serif',
          '& .MuiInputBase-root': {
            fontFamily: 'Manrope, sans-serif',
            fontSize: 'var(--font-size-base)',
            fontWeight: 500,
            lineHeight: '1.25rem',
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '4px',
            border: '1px solid',
            borderColor: theme.palette.divider,
            height: '48px',
            padding: '0 12px',
            transition: 'border-color 200ms ease-in-out, background-color 200ms ease-in-out',
            '&::placeholder': {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
            '&.Mui-focused': {
              borderColor: theme.palette.primary.main,
            },
            '&:hover:not(.Mui-focused)': {
              borderColor: theme.palette.primary.main,
            },
          },
          '& .MuiInputBase-input': {
            padding: 0,
            height: '100%',
            '&::placeholder': {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
          },
          '& .MuiInput-underline': {
            '&::before': {
              display: 'none',
            },
            '&::after': {
              display: 'none',
            },
          },
          '&[data-variant="underline"]': {
            '& .MuiInputBase-root': {
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderRadius: 0,
              padding: 0,
              height: '48px',
              minHeight: '48px',
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'var(--font-size-base)',
              fontWeight: 400,
              lineHeight: '1.25rem',
              letterSpacing: '0.005em',
              color: theme.palette.text.secondary,
              transition: 'border-bottom-color 200ms ease-in-out',
              '&::before': {
                display: 'none',
              },
              '&::after': {
                display: 'none',
              },
              '&:hover:not(.Mui-disabled)': {
                backgroundColor: 'transparent',
                borderBottomColor: theme.palette.primary.main,
                '&::before': {
                  display: 'none',
                },
              },
              '&.Mui-focused': {
                backgroundColor: 'transparent',
              },
              '&.Mui-disabled': {
                borderBottomColor: theme.palette.action.disabled,
              },
              '&.MuiInputBase-adornedStart': {
                color: theme.palette.text.secondary,
              },
            },
            '& .MuiInputBase-input': {
              padding: 0,
              cursor: 'text',
              color: theme.palette.text.secondary,
              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 1,
              },
            },
            '& .MuiInput-underline': {
              '&::before': {
                display: 'none',
              },
              '&::after': {
                display: 'none',
              },
            },
            '&.Mui-disabled': {
              '& .MuiInputBase-root': {
                color: theme.palette.action.disabled,
              },
              '& .MuiInputBase-input': {
                cursor: 'not-allowed',
                color: theme.palette.action.disabled,
              },
            },
          },
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          '&.Mui-checked': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiInputBase-root': {
            fontFamily: 'Manrope, sans-serif',
            fontSize: 'var(--font-size-base)',
            fontWeight: 500,
            lineHeight: '1.25rem',
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '4px',
            border: '1px solid',
            borderColor: theme.palette.divider,
            height: '48px',
            padding: '0 12px',
            transition: 'border-color 200ms ease-in-out, background-color 200ms ease-in-out',
            '&:hover:not(.Mui-focused)': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused': {
              borderColor: theme.palette.primary.main,
            },
          },
          '& .MuiInputBase-input': {
            padding: 0,
            height: '100%',
          },
          '& .MuiInput-underline': {
            '&::before': {
              display: 'none',
            },
            '&::after': {
              display: 'none',
            },
          },
          '&[data-variant="underline"]': {
            '& .MuiInputBase-root': {
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderRadius: 0,
              padding: 0,
              height: '48px',
              minHeight: '48px',
              transition: 'border-bottom-color 200ms ease-in-out',
              '&:hover:not(.Mui-disabled)': {
                backgroundColor: 'transparent',
                borderBottomColor: theme.palette.primary.main,
              },
              '&.Mui-focused': {
                backgroundColor: 'transparent',
              },
              '&.Mui-disabled': {
                borderBottomColor: theme.palette.action.disabled,
              },
            },
            '& .MuiInput-underline': {
              '&::before': {
                display: 'none',
              },
              '&::after': {
                display: 'none',
              },
            },
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: 'Manrope, sans-serif',
          fontSize: 'var(--font-size-base)',
        },
        input: {
          fontSize: 'var(--font-size-base)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: 'Manrope, sans-serif',
          fontSize: 'var(--font-size-base)',
        },
        input: {
          fontSize: 'var(--font-size-base)',
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          fontFamily: 'Manrope, sans-serif',
          fontSize: 'var(--font-size-base)',
        },
        input: {
          fontSize: 'var(--font-size-base)',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiSelect-select": {
            height: '100% !important',
          },
          fontFamily: 'Manrope, sans-serif',
          fontSize: 'var(--font-size-base)',
          fontWeight: 400,
          lineHeight: '1.25rem',
          letterSpacing: '0.005em',
          height: '48px',
          '&.Mui-disabled': {
            cursor: 'not-allowed',
            '& .MuiSelect-select': {
              cursor: 'not-allowed',
            },
          },
          '&[data-variant="underline"]': {
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: 0,
            padding: 0,
            height: '48px',
            minHeight: '48px',
            '& .MuiSelect-select': {
              padding: 0,
              paddingRight: '24px !important',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              height: '100%',
              minHeight: '48px',
              color: theme.palette.text.secondary,
              '&:focus': {
                backgroundColor: 'transparent',
              },
            },
            '& .MuiSelect-icon': {
              color: theme.palette.text.secondary,
              right: 0,
              fontSize: '1.25rem',
            },
            '&.Mui-disabled': {
              cursor: 'not-allowed',
              '& .MuiSelect-select': {
                color: theme.palette.action.disabled,
                cursor: 'not-allowed',
              },
              '& .MuiSelect-icon': {
                color: theme.palette.action.disabled,
              },
            },
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: 'transparent',
            },
            '&.Mui-focused': {
              backgroundColor: 'transparent',
            },
          },
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          variants: ALERT_VARIANTS.map((variant) => ({
            props: { variant },
            style: variantLinearProgressStyles(theme, variant),
          })),
        }),
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: alertIconMapping,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: 'Manrope, sans-serif',
          variants: ALERT_VARIANTS.map((variant) => ({
            props: { severity: variant, variant: 'standard' },
            style: alertStyles(theme, variant),
          })),
          [`&${customizedAlertClassExclusions}`]: {
            '& .MuiAlert-icon': {
              color: 'inherit',
              '& .MuiSvgIcon-root': {
                color: 'inherit',
              },
            },
          },
        }),
        icon: ({ ownerState }) => {
          const severity = (ownerState.colorSeverity ?? ownerState.severity) as AlertColor | undefined;
          if (severity && ALERT_VARIANTS.includes(severity)) {
            return {};
          }
          return {
            color: 'inherit',
            '& .MuiSvgIcon-root': {
              color: 'inherit',
            },
          };
        },
        standardSuccess: ({ theme }) => alertStyles(theme, 'success'),
        standardInfo: ({ theme }) => alertStyles(theme, 'info'),
        standardWarning: ({ theme }) => alertStyles(theme, 'warning'),
        standardError: ({ theme }) => alertStyles(theme, 'error'),
      },
    },
  },
});

// Export the theme - responsive font sizes are handled via CSS custom properties
export const muiTheme = baseTheme;

